import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  Search, 
  Filter, 
  ChevronRight,
  Truck,
  CheckCircle2,
  Clock,
  XCircle,
  X,
  MapPin,
  User as UserIcon,
  Mail,
  CreditCard,
  Check,
  Printer
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../config';

const OrderListScreen = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { getConfig } = useAuth();

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_URL}/orders`, getConfig());
      setOrders(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching orders:', error);
      setLoading(false);
    }
  }, [getConfig]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleOpenModal = (order) => {
    setSelectedOrder(order);
    setShowModal(true);
  };

  const handleMarkAsDelivered = async (id) => {
    try {
      setIsSubmitting(true);
      const { data } = await axios.put(`${API_URL}/orders/${id}/deliver`, {}, getConfig());
      // Update local orders list
      setOrders(prev => prev.map(o => o._id === id ? { ...o, isDelivered: data.isDelivered, deliveredAt: data.deliveredAt } : o));
      // Update selected order in modal
      setSelectedOrder(prev => ({ ...prev, isDelivered: data.isDelivered, deliveredAt: data.deliveredAt }));
      alert('Order marked as delivered!');
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update order status');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Delivered': return <CheckCircle2 className="h-4 w-4 text-emerald-500" />;
      case 'Processing': return <Clock className="h-4 w-4 text-amber-500" />;
      case 'Shipped': return <Truck className="h-4 w-4 text-blue-500" />;
      default: return <XCircle className="h-4 w-4 text-red-500" />;
    }
  };

  return (
    <div className="p-8 space-y-8 max-w-7xl mx-auto">
      <div className="flex justify-between items-center">
        <div className="space-y-1">
          <h1 className="text-3xl font-black italic text-secondary-900 uppercase">Order Management</h1>
          <p className="text-secondary-500 font-medium">Monitor and manage customer orders and shipments.</p>
        </div>
      </div>

      <div className="bg-white p-4 rounded-3xl border border-secondary-100 flex items-center justify-between shadow-sm">
        <div className="relative w-96">
          <Search className="absolute left-4 top-3 text-secondary-400 h-5 w-5" />
          <input 
            type="text" 
            placeholder="Search by Order ID or Customer..." 
            className="w-full bg-secondary-50 border-none rounded-2xl py-3 pl-12 pr-4 focus:ring-2 focus:ring-primary-500 outline-none font-bold"
          />
        </div>
        <div className="flex items-center space-x-3">
           <button className="flex items-center space-x-2 px-4 py-2 rounded-xl bg-secondary-50 text-secondary-600 font-bold hover:bg-secondary-100 transition-all">
             <Filter className="h-4 w-4" />
             <span>Filter</span>
           </button>
        </div>
      </div>

      <div className="bg-white rounded-[40px] border border-secondary-100 shadow-sm overflow-hidden">
        <table className="w-full text-left">
          <thead className="bg-secondary-50/50">
            <tr className="border-b border-secondary-100">
              <th className="px-8 py-5 font-black text-secondary-400 text-[10px] uppercase tracking-widest">Order ID</th>
              <th className="px-8 py-5 font-black text-secondary-400 text-[10px] uppercase tracking-widest">Customer</th>
              <th className="px-8 py-5 font-black text-secondary-400 text-[10px] uppercase tracking-widest">Total</th>
              <th className="px-8 py-5 font-black text-secondary-400 text-[10px] uppercase tracking-widest">Status</th>
              <th className="px-8 py-5 font-black text-secondary-400 text-[10px] uppercase tracking-widest">Date</th>
              <th className="px-8 py-5 text-right font-black text-secondary-400 text-[10px] uppercase tracking-widest">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-secondary-50">
            {loading ? (
               <tr><td colSpan="6" className="text-center py-20 font-bold text-secondary-400">Loading orders...</td></tr>
            ) : orders.length === 0 ? (
               <tr><td colSpan="6" className="text-center py-20 font-bold text-secondary-400 uppercase tracking-widest">No orders found</td></tr>
            ) : orders.map((order) => (
              <tr key={order._id} className="hover:bg-secondary-50/50 transition-colors group">
                <td className="px-8 py-5 font-bold text-secondary-900 text-xs">#{order._id}</td>
                <td className="px-8 py-5">
                  <div className="space-y-0.5">
                    <p className="font-bold text-secondary-900">{order.user?.name || 'Guest User'}</p>
                    <p className="text-xs text-secondary-400 font-medium">{order.user?.email || 'No Email'}</p>
                  </div>
                </td>
                <td className="px-8 py-5 font-black text-secondary-900">₹{order.totalPrice}</td>
                <td className="px-8 py-5">
                  <div className="flex items-center space-x-2">
                    {getStatusIcon(order.isDelivered ? 'Delivered' : 'Processing')}
                    <span className={`text-[10px] font-black uppercase tracking-widest ${order.isDelivered ? 'text-emerald-600' : 'text-amber-600'}`}>
                      {order.isDelivered ? 'Delivered' : 'Processing'}
                    </span>
                  </div>
                </td>
                <td className="px-8 py-5 text-secondary-500 font-medium text-xs">
                  {new Date(order.createdAt).toLocaleDateString()}
                </td>
                <td className="px-8 py-5 text-right">
                  <button 
                    onClick={() => handleOpenModal(order)}
                    className="p-2 text-secondary-400 hover:text-primary-600 hover:bg-primary-50 rounded-xl transition-all"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Order Detail Modal */}
      {showModal && selectedOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-secondary-900/60 backdrop-blur-sm overflow-y-auto">
          <div className="bg-white rounded-[40px] w-full max-w-4xl my-8 overflow-hidden shadow-2xl animate-in fade-in zoom-in duration-300">
            <div className="p-8 md:p-12 space-y-8">
              
              {/* Modal Header */}
              <div className="flex justify-between items-center pb-6 border-b border-secondary-100">
                <div>
                  <h2 className="text-2xl font-black italic text-secondary-900 uppercase tracking-tighter">Order Details</h2>
                  <p className="text-secondary-400 text-xs font-mono font-bold mt-1">ID: #{selectedOrder._id}</p>
                </div>
                <div className="flex items-center space-x-3">
                  <a
                    href={`/orders/${selectedOrder._id}/invoice`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-2 bg-secondary-50 hover:bg-secondary-100 border border-secondary-200 text-secondary-700 px-4 py-2.5 rounded-2xl font-bold transition-all text-xs"
                  >
                    <Printer className="h-4 w-4" />
                    <span>Print Invoice</span>
                  </a>
                  <button onClick={() => setShowModal(false)} className="p-3 hover:bg-secondary-100 rounded-2xl transition-all">
                    <X className="h-6 w-6 text-secondary-400" />
                  </button>
                </div>
              </div>

              {/* Modal Body */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                
                {/* Column 1 & 2: Items and Customer info */}
                <div className="md:col-span-2 space-y-8">
                  
                  {/* Customer Information */}
                  <div className="bg-secondary-50/50 p-6 rounded-3xl space-y-4">
                    <h3 className="font-bold text-secondary-900 uppercase tracking-widest text-[10px]">Customer Details</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="flex items-center space-x-3 text-secondary-600">
                        <UserIcon className="h-4 w-4 text-primary-600" />
                        <span className="text-sm font-bold text-secondary-900">{selectedOrder.user?.name || 'Guest User'}</span>
                      </div>
                      <div className="flex items-center space-x-3 text-secondary-600">
                        <Mail className="h-4 w-4 text-primary-600" />
                        <span className="text-sm font-medium">{selectedOrder.user?.email || 'No email'}</span>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-3 text-secondary-600 pt-2 border-t border-secondary-100">
                      <MapPin className="h-4 w-4 text-primary-600 mt-1 flex-shrink-0" />
                      <div className="text-sm font-medium">
                        <p className="font-bold text-secondary-800">Shipping Address</p>
                        <p className="text-secondary-500">{selectedOrder.shippingAddress.address}</p>
                        <p className="text-secondary-500">{selectedOrder.shippingAddress.city}, {selectedOrder.shippingAddress.postalCode}</p>
                        <p className="text-secondary-500">{selectedOrder.shippingAddress.country}</p>
                      </div>
                    </div>
                  </div>

                  {/* Items list */}
                  <div className="space-y-4">
                    <h3 className="font-bold text-secondary-900 uppercase tracking-widest text-[10px]">Items List</h3>
                    <div className="divide-y divide-secondary-100">
                      {selectedOrder.orderItems.map((item, index) => (
                        <div key={index} className="py-4 flex items-center space-x-4">
                          <img src={item.image} alt={item.name} className="w-16 h-16 rounded-xl object-cover border border-secondary-100" />
                          <div className="flex-1">
                            <p className="font-bold text-secondary-900 text-sm line-clamp-1">{item.name}</p>
                            <p className="text-xs text-secondary-400 font-bold">{item.qty} × ₹{item.price}</p>
                          </div>
                          <p className="font-black text-secondary-900 text-sm">₹{item.qty * item.price}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Column 3: Order Summary & Status */}
                <div className="bg-secondary-50 p-6 rounded-[32px] space-y-6 flex flex-col justify-between">
                  <div className="space-y-6">
                    <h3 className="font-bold text-secondary-900 uppercase tracking-widest text-[10px]">Summary</h3>
                    
                    {/* Prices */}
                    <div className="space-y-3 pb-6 border-b border-secondary-200">
                      <div className="flex justify-between text-sm text-secondary-500 font-medium">
                        <span>Items Subtotal</span>
                        <span>₹{selectedOrder.itemsPrice}</span>
                      </div>
                      {selectedOrder.discountPrice > 0 && (
                        <div className="flex justify-between text-sm text-red-500 font-bold">
                          <span>Discount ({selectedOrder.promoCode})</span>
                          <span>-₹{selectedOrder.discountPrice}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-sm text-secondary-500 font-medium">
                        <span>Shipping</span>
                        <span className="text-emerald-600 font-bold">FREE</span>
                      </div>
                      <div className="flex justify-between text-lg font-black text-secondary-900 pt-3 border-t border-secondary-200">
                        <span>Total</span>
                        <span>₹{selectedOrder.totalPrice}</span>
                      </div>
                    </div>

                    {/* Payment Info */}
                    <div className="space-y-3">
                      <h4 className="text-[10px] font-black uppercase tracking-wider text-secondary-400 flex items-center space-x-2">
                        <CreditCard className="h-4 w-4" />
                        <span>Payment Info</span>
                      </h4>
                      <p className="text-sm font-bold text-secondary-800">{selectedOrder.paymentMethod}</p>
                      <div className={`inline-flex items-center space-x-1.5 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${
                        selectedOrder.isPaid ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {selectedOrder.isPaid ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                        <span>{selectedOrder.isPaid ? 'Paid' : 'Unpaid / Pending'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Delivery Actions */}
                  <div className="pt-6 border-t border-secondary-200 space-y-3">
                    <h4 className="text-[10px] font-black uppercase tracking-wider text-secondary-400">Delivery Status</h4>
                    {selectedOrder.isDelivered ? (
                      <div className="bg-emerald-100 text-emerald-700 p-4 rounded-2xl flex items-start space-x-3">
                        <CheckCircle2 className="h-5 w-5 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-bold text-sm">Delivered</p>
                          <p className="text-[10px] opacity-75">{new Date(selectedOrder.deliveredAt).toLocaleString()}</p>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="bg-amber-100 text-amber-800 p-4 rounded-2xl flex items-start space-x-3">
                          <Clock className="h-5 w-5 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="font-bold text-sm">Processing</p>
                            <p className="text-[10px] opacity-75">Awaiting shipment & delivery.</p>
                          </div>
                        </div>
                        <button 
                          onClick={() => handleMarkAsDelivered(selectedOrder._id)}
                          disabled={isSubmitting}
                          className="w-full bg-primary-600 hover:bg-primary-700 text-white py-3.5 rounded-2xl font-black italic uppercase transition-all disabled:opacity-50 text-center text-sm shadow-lg shadow-primary-200"
                        >
                          {isSubmitting ? 'Updating...' : 'Mark as Delivered'}
                        </button>
                      </div>
                    )}
                  </div>
                  
                </div>

              </div>

            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default OrderListScreen;

